import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import MemberInitialsWithCircle from '@atoms/MemberInitialsWithCircle';
import { BodyMedium, BodySmall } from '@styled';

import { formatMoney } from '@general/helpers/currency.helpers';
import productTypes from '@general/productTypes';
import useCartFromAPI from '@hooks/apiLegacy/useCartFromAPI';
import useViewer from '@hooks/useViewer';
import { mediaQueries } from '@general/breakpoints';

const ContainerStyled = styled.div`
    &:not(:last-child) {
        margin-bottom: 16px;
    }
`;

const EventNameContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;

    @media ${mediaQueries.lg} {
        align-items: center;
        flex-direction: row;
        gap: 0.5rem;
    }
`;

const ItemDescriptionContainerStyled = styled.div`
    margin-left: 10px;

    &:not(:last-child) {
        margin-bottom: 16px;
    }
`;

const getTotalPriceForMembership = (membershipItem, cartLocale) => {
    const mainDuesItem = membershipItem.dues.find(dues => dues.product_type === 'dues');
    return formatMoney(mainDuesItem?.price || 0, cartLocale);

    // return formatMoney(
    //     membershipItem.dues.reduce((total, item) => {
    //         return total + item.price;
    //     }, 0),
    //     cartLocale
    // );
};

const getTaxForMembership = (membershipItem, cartLocale) => {
    const mainDuesItem = membershipItem.dues.find(dues => dues.product_type === 'dues');
    const { tax } = mainDuesItem;

    if (!tax) return null;

    return (
        <div className="flex justify-between">
            <div className="flex flex-column">
                <BodyMedium>Tax</BodyMedium>
            </div>
            <BodyMedium className="ml-1rem">
                <>{formatMoney(tax, cartLocale)}</>
            </BodyMedium>
        </div>
    );
};

const displayEventDdate = lineItem => {
    const { begins_at, ends_at } = lineItem.sessions[0].meta_info;

    if (begins_at == null || ends_at == null) return null;

    // return getDateRangeTextInEastern(begins_at, ends_at);

    const [begins_fmt, ends_fmt] = [begins_at, ends_at].map(date =>
        new Date(date).toLocaleDateString(navigator.language, { day: '2-digit', month: '2-digit', year: '2-digit' })
    );

    return `(${begins_fmt} - ${ends_fmt})`;
};

const getDiscountsForMembership = (membershipItem, cartLocale) => {
    const mainDuesItem = membershipItem.dues.find(dues => dues.product_type === 'dues');
    const discounts = mainDuesItem.discounts.map(discount => ({
        promotional_code: discount.promotional_code,
        promotional_discount: discount.promotional_discount,
        promotional_discount_description: discount.promotional_discount_description,
        type: discount.type,
    }));

    return discounts.length > 0
        ? discounts.map(discount => {
              let label = `Discount: ${discount.promotional_code}`;

              if (discount.type === 'auto') {
                  label = discount.promotional_discount_description;
              }

              return (
                  <div key={discount.promotional_code} className="flex justify-between">
                      <div className="flex flex-column">
                          <BodySmall className="text-admiral">{label}</BodySmall>
                      </div>
                      <BodySmall className="ml-1rem text-admiral">
                          <>-{formatMoney(discount.promotional_discount, cartLocale)}</>
                      </BodySmall>
                  </div>
              );
          })
        : null;
};

const getTaxForEvent = (eventItem, cartLocale) => {
    const { tax } = eventItem.sessions[0];

    if (!tax || tax <= 0) return null;

    return (
        <div className="flex justify-between">
            <div className="flex flex-column">
                <BodySmall>Tax</BodySmall>
            </div>
            <BodySmall className="ml-1rem">
                <>{formatMoney(tax, cartLocale)}</>
            </BodySmall>
        </div>
    );
};

const getDiscountsForEvent = (eventItem, cartLocale) => {
    const sessionDiscounts = eventItem.sessions.flatMap(session =>
        session.discounts.map(discount => ({
            promotional_code: discount.promotional_code,
            promotional_discount: discount.promotional_discount,
            promotional_discount_description: discount.promotional_discount_description,
            type: discount.type,
        }))
    );

    return sessionDiscounts.length > 0
        ? sessionDiscounts.map(discount => {
              return (
                  <div key={discount.promotional_code} className="flex justify-between">
                      <div className="flex flex-column">
                          <BodySmall className="text-admiral">
                              {discount.type === 'event_credit'
                                  ? 'Event Credit'
                                  : discount.promotional_discount_description ||
                                    `Discount: ${discount.promotional_code}`}
                          </BodySmall>
                      </div>
                      <BodySmall className="ml-1rem text-admiral">
                          <>-{formatMoney(discount.promotional_discount, cartLocale)}</>
                      </BodySmall>
                  </div>
              );
          })
        : null;
};
const getTotalPriceForEvent = (eventItem, cartLocale) => {
    const mainEventItem = eventItem.sessions.find(session => session.product_type === 'event_fee');
    return formatMoney(mainEventItem?.price || 0, cartLocale);

    // return formatMoney(
    //     eventItem.sessions.reduce((total, item) => {
    //         return total + item.price;
    //     }, 0),
    //     cartLocale
    // );
};

const CartSummaryMember = ({ memberCompany, memberId, memberName, itemsGroupedByType, isReceiptView }) => {
    const { viewer } = useViewer();
    const { cart } = useCartFromAPI({ viewerId: viewer?.recordId });

    if (!cart) {
        return null;
    }

    const memberNameSplit = memberName.split(' ');

    return (
        <ContainerStyled key={memberId}>
            <div className="flex">
                <MemberInitialsWithCircle
                    firstName={memberNameSplit.length > 0 ? memberNameSplit[0] : ''}
                    lastName={memberNameSplit.length > 1 ? memberNameSplit[memberNameSplit.length - 1] : ''}
                />

                <div className="w-full">
                    <div className="ml-0.625rem pb-0.5rem">
                        <BodyMedium>
                            {memberName} {memberCompany ? `(${memberCompany})` : null}
                        </BodyMedium>
                    </div>
                    {itemsGroupedByType.map(itemTypeGroup => {
                        return (
                            <React.Fragment key={itemTypeGroup.type}>
                                {itemTypeGroup.items.map(item => {
                                    return (
                                        <ItemDescriptionContainerStyled key={item.product_id}>
                                            {itemTypeGroup.type === productTypes.MEMBERSHIP && (
                                                <>
                                                    <div className="flex justify-between">
                                                        <div className="flex flex-column">
                                                            <BodyMedium>{item.name}</BodyMedium>
                                                        </div>
                                                        <BodyMedium className="ml-1rem">
                                                            <>{getTotalPriceForMembership(item, cart.locale)}</>
                                                        </BodyMedium>
                                                    </div>
                                                    {getTaxForMembership(item, cart.locale)}
                                                    {getDiscountsForMembership(item, cart.locale)}
                                                </>
                                            )}
                                            <div className="mb-0.5rem" />
                                            {itemTypeGroup.type === productTypes.EVENT && (
                                                <>
                                                    <div className="flex justify-between">
                                                        <EventNameContainer>
                                                            <BodyMedium>{item.name}</BodyMedium>
                                                            {isReceiptView && (
                                                                <BodySmall>{displayEventDdate(item)}</BodySmall>
                                                            )}
                                                        </EventNameContainer>
                                                        <BodyMedium className="ml-1rem">
                                                            <>{getTotalPriceForEvent(item, cart.locale)}</>
                                                        </BodyMedium>
                                                    </div>
                                                    {getTaxForEvent(item, cart.locale)}
                                                    {getDiscountsForEvent(item, cart.locale)}
                                                </>
                                            )}
                                            {itemTypeGroup.type === productTypes.MEMBERSHIP &&
                                                item.dues
                                                    .filter(dues => dues.product_type !== 'dues')
                                                    .map(dues => {
                                                        return (
                                                            <div key={dues.id} className="flex justify-between">
                                                                <BodySmall className="text-dark-gray">
                                                                    {dues.description}
                                                                </BodySmall>

                                                                <BodySmall className="ml-1rem text-dark-gray">
                                                                    {formatMoney(dues.price, cart.locale)}
                                                                </BodySmall>
                                                            </div>
                                                        );
                                                    })}

                                            {itemTypeGroup.type === productTypes.EVENT &&
                                                item.sessions
                                                    .filter(session => session.product_type !== 'event_fee')
                                                    .map(session => {
                                                        return (
                                                            <div key={session.id} className="flex justify-between">
                                                                <BodySmall className="text-dark-gray">
                                                                    {session.meta_info.event_function_title}
                                                                </BodySmall>

                                                                <BodySmall className="ml-1rem text-dark-gray">
                                                                    {formatMoney(session.price, cart.locale)}
                                                                </BodySmall>
                                                            </div>
                                                        );
                                                    })}
                                        </ItemDescriptionContainerStyled>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>
        </ContainerStyled>
    );
};

CartSummaryMember.propTypes = {
    memberCompany: PropTypes.string,
    memberId: PropTypes.number.isRequired,
    memberName: PropTypes.string.isRequired,
    isReceiptView: PropTypes.bool,
    itemsGroupedByType: PropTypes.arrayOf(
        PropTypes.shape({
            type: PropTypes.oneOf([productTypes.MEMBERSHIP, productTypes.EVENT]).isRequired,
            items: PropTypes.arrayOf(PropTypes.object).isRequired,
        })
    ),
};

export default CartSummaryMember;
